import { useState } from 'react'
import { Form, FormSpy } from 'react-final-form'

import ConfirmationModal from 'common/components/confirmationModal'
import Drawer from 'common/components/drawer'
import useDisablePointerEventsWhenModalVisible from 'common/components/drawer/useDisablePointerEventsWhenModalVisible'

import type { AdvancedLanguageRuleBody } from '../../api/api'
import useCreateAdvancedLanguageRuleMutation from '../../api/mutations/useCreateAdvancedLanguageRuleMutation'

import AdvancedLanguageCancelButton from './AdvancedLanguageCancelButton'
import AdvancedLanguageSubmitButton from './AdvancedLanguageSubmitButton'
import FormContent from './FormContent'

const AddAdvancedLanguageRulesDrawer = ({ isOpen, onOpenChange }) => {
  const { mutate, isLoading } = useCreateAdvancedLanguageRuleMutation()
  const [isFormDirty, setFormDirty] = useState<boolean>(false)
  const [isConfirmationVisible, setIsConfirmationVisible] =
    useState<boolean>(false)

  const handleSave = (values: AdvancedLanguageRuleBody) =>
    mutate(values, {
      onSuccess: () => {
        onOpenChange(false)
      },
    })

  useDisablePointerEventsWhenModalVisible(isConfirmationVisible)

  return (
    <>
      <Drawer
        isOpen={isOpen}
        title="Create advanced language rule"
        dismissible={!isConfirmationVisible}
        onOpenChange={onOpenChange}
        content={
          <div className="w-219 flex flex-col h-full">
            <Form
              onSubmit={handleSave}
              render={({ handleSubmit }) => {
                return (
                  <form
                    onSubmit={handleSubmit}
                    className="flex flex-col flex-1 h-full"
                  >
                    <FormSpy
                      onChange={(props) => {
                        const dirtyFields = Object.keys(props.dirtyFields)

                        setFormDirty(dirtyFields.length > 0)
                      }}
                    />

                    <FormContent
                      title="Add brand voice instruction"
                      description="Use brand voice instructions to keep language generation on-brand."
                    />

                    <div className="flex flex-none justify-end mt-auto w-full py-6 border-t border-gold-400 gap-3">
                      <AdvancedLanguageCancelButton
                        data-testid="advanced-language-cancel-button"
                        onClick={() => {
                          if (isFormDirty) {
                            setIsConfirmationVisible(true)
                          } else {
                            onOpenChange(false)
                          }
                        }}
                      />
                      <AdvancedLanguageSubmitButton
                        data-testid="advanced-language-submit-button"
                        isLoading={isLoading}
                      >
                        Done
                      </AdvancedLanguageSubmitButton>
                    </div>
                  </form>
                )
              }}
            />
          </div>
        }
      />
      <ConfirmationModal
        title="Are you sure you want to leave without saving?"
        confirmationText="Any unsaved instructions and examples will be lost."
        confirmButtonText="Yes, I want to leave"
        cancelButtonText="Go back"
        open={isConfirmationVisible}
        hasConfirmationSlider={false}
        onConfirm={() => {
          onOpenChange(false)
          setIsConfirmationVisible(false)
        }}
        onCancel={() => {
          setIsConfirmationVisible(false)
        }}
      />
    </>
  )
}

export default AddAdvancedLanguageRulesDrawer
